import React, { useMemo } from "react";
import { ArrowLeft, Printer } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { LANE_STATUS_META } from "./config/stakeholderLaneConfig";
import { buildWeeklyOperatingReport } from "./reporting/buildWeeklyOperatingReport";

const statusLabel = (status) => LANE_STATUS_META[status]?.label || "Watch";

function ReportList({ items, emptyText, renderItem }) {
  if (!items?.length) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">{emptyText}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div
          className="rounded-2xl border border-slate-200/80 bg-white/85 p-4 shadow-sm dark:border-white/10 dark:bg-white/[0.04]"
          key={item.id}
        >
          {renderItem(item)}
        </div>
      ))}
    </div>
  );
}

function StakeholderItemSummary({ item }) {
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {item.report_summary || item.reason}
          </p>
        </div>
        {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
      </div>
      {item.report_context ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {item.report_context.split(" | ").map((tag) => (
            <Badge key={tag} variant="outline">
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
      {item.next_move ? (
        <p className="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-cyan-700 dark:text-aqua">
          Next: {item.next_move}
        </p>
      ) : null}
      {item.last_meaningful_movement ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.last_meaningful_movement}</p>
      ) : null}
      {item.report_contact ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.report_contact}</p>
      ) : null}
    </>
  );
}

export default function StakeholderWeeklyReport({ board, summary }) {
  const navigate = useNavigate();
  const report = useMemo(() => buildWeeklyOperatingReport({ board, summary }), [board, summary]);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.12),_transparent_24%),linear-gradient(180deg,#f8fafc_0%,#eef2f7_100%)] pb-10 dark:bg-[radial-gradient(circle_at_top_left,_rgba(34,211,238,0.09),_transparent_24%),linear-gradient(180deg,#05080d_0%,#0a1118_100%)]">
      <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-[30px] border border-slate-200/80 bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:border-white/10 dark:bg-[#081018]/90">
          <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 dark:border-white/10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-700 dark:text-aqua">
                Community Weekly Report
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                Week of {report.generatedLabel}
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                This report is generated from the same community board truth so names, movement, drift, and
                next moves stay aligned.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={() => navigate("/community")} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Community Home
              </Button>
              <Button onClick={() => window.print()} variant="outline">
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-3xl border border-rose-200/70 bg-rose-50/80 p-5 dark:border-rose-400/20 dark:bg-rose-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-800 dark:text-rose-100">
                What Needs Kevin Now
              </p>
              <p className="mt-3 text-3xl font-semibold text-rose-950 dark:text-white">
                {report.topLine.needsKevinNow.length}
              </p>
              <p className="mt-2 text-sm text-rose-800 dark:text-rose-100">
                {report.topLine.needsKevinNow[0]?.report_summary || report.topLine.needsKevinNow[0]?.title || "No urgent items surfaced."}
              </p>
            </div>

            <div className="rounded-3xl border border-emerald-200/70 bg-emerald-50/80 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-800 dark:text-emerald-100">
                Biggest Win
              </p>
              <p className="mt-3 text-base font-semibold text-emerald-950 dark:text-white">
                {report.topLine.biggestWin?.title || "No clear win surfaced yet."}
              </p>
              <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-100">
                {report.topLine.biggestWin?.report_summary || report.topLine.biggestWin?.reason || "Movement will appear here when the board carries it."}
              </p>
            </div>

            <div className="rounded-3xl border border-amber-200/70 bg-amber-50/80 p-5 dark:border-amber-400/20 dark:bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-800 dark:text-amber-100">
                Biggest Risk
              </p>
              <p className="mt-3 text-base font-semibold text-amber-950 dark:text-white">
                {report.topLine.biggestRisk?.title || "No watch signals are surfaced yet."}
              </p>
              <p className="mt-2 text-sm text-amber-800 dark:text-amber-100">
                {report.topLine.biggestRisk?.report_summary || report.topLine.biggestRisk?.reason || "The report will pull the clearest drift signal here."}
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Financial Status
              </p>
              <p className="mt-3 text-base font-semibold text-slate-950 dark:text-white">
                {statusLabel(report.topLine.overallStatus)}
              </p>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                {report.topLine.financialStatus}
              </p>
            </div>
          </div>

          <section className="mt-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-950 dark:text-white">Needs Kevin Now</h2>
                <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                  Ranked directly from the current board state.
                </p>
              </div>
              {report.topLine.mostNeglectedLane ? (
                <Badge variant="outline">
                  Most neglected lane: {report.topLine.mostNeglectedLane.title}
                </Badge>
              ) : null}
            </div>

            <div className="mt-4">
              <ReportList
                emptyText="No urgent items are currently flagged."
                items={report.topLine.needsKevinNow}
                renderItem={(item) => <StakeholderItemSummary item={item} />}
              />
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950 dark:text-white">Lane by Lane</h2>
            <div className="mt-4 space-y-5">
              {report.laneSections.map((lane) => (
                <div
                  className="rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03]"
                  key={lane.id}
                >
                  <div className="flex flex-col gap-3 border-b border-slate-200/80 pb-4 dark:border-white/10 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-950 dark:text-white">{lane.title}</h3>
                      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{lane.interpretation}</p>
                    </div>
                    <Badge variant="outline">{statusLabel(lane.status)}</Badge>
                  </div>

                  <div className="mt-4 grid gap-4 xl:grid-cols-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700 dark:text-rose-100">
                        Needs action
                      </p>
                      <div className="mt-3 space-y-3">
                        {lane.needsAction.length ? (
                          lane.needsAction.map((item) => (
                            <div className="rounded-2xl border border-rose-200/70 bg-white/90 p-3 dark:border-rose-400/20 dark:bg-white/[0.04]" key={item.id}>
                              <StakeholderItemSummary item={item} />
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No urgent items are surfaced in this lane.
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-100">
                        Moving
                      </p>
                      <div className="mt-3 space-y-3">
                        {lane.moving.length ? (
                          lane.moving.map((item) => (
                            <div className="rounded-2xl border border-emerald-200/70 bg-white/90 p-3 dark:border-emerald-400/20 dark:bg-white/[0.04]" key={item.id}>
                              <StakeholderItemSummary item={item} />
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No movement is being reinforced yet.
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-100">
                        Watch
                      </p>
                      <div className="mt-3 space-y-3">
                        {lane.watch.length ? (
                          lane.watch.map((item) => (
                            <div className="rounded-2xl border border-amber-200/70 bg-white/90 p-3 dark:border-amber-400/20 dark:bg-white/[0.04]" key={item.id}>
                              <StakeholderItemSummary item={item} />
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No drift signals are currently being watched.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-200/80 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.04]">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Recommended Kevin move
                    </p>
                    <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{lane.recommendedMove}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="mt-10 grid gap-5 xl:grid-cols-2">
            <section className="rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03]">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Wins Worth Reinforcing</h2>
              <div className="mt-4">
                <ReportList
                  emptyText="No wins are surfaced yet."
                  items={report.winsWorthReinforcing}
                  renderItem={(item) => <StakeholderItemSummary item={item} />}
                />
              </div>
            </section>

            <section className="rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03]">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Drift To Prevent</h2>
              <div className="mt-4">
                <ReportList
                  emptyText="No drift items are surfaced yet."
                  items={report.driftToPrevent}
                  renderItem={(item) => <StakeholderItemSummary item={item} />}
                />
              </div>
            </section>
          </div>

          <div className="mt-10 grid gap-5 xl:grid-cols-2">
            <section className="rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03]">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">This Week&apos;s Non-Negotiables</h2>
              <div className="mt-4 space-y-3">
                {report.nonNegotiables.map((item) => (
                  <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.04]" key={item.id}>
                    <p className="text-sm text-slate-700 dark:text-slate-200">{item.label}</p>
                    {item.context ? (
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.context}</p>
                    ) : null}
                    {item.contact ? (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.contact}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03]">
              <h2 className="text-lg font-semibold text-slate-950 dark:text-white">Questions For Kevin</h2>
              <div className="mt-4 space-y-3">
                {report.questionsForKevin.map((question) => (
                  <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 dark:border-white/10 dark:bg-white/[0.04]" key={question}>
                    <p className="text-sm text-slate-700 dark:text-slate-200">{question}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
